import React, { CSSProperties, FC, useMemo } from 'react';

import { Helpdesk } from './Helpdesk';
import { useResolvedHelpdeskBottomOffset } from './usePlaceHelpdeskAboveElement';
import styles from './styles.module.css';

const HELPDESK_ANCHOR_PRESETS = {
  default: {
    fallbackBottom: 120,
  },
  messenger: {
    fallbackBottom: 30,
  },
  ada: {
    fallbackBottom: 0,
  },
};

type HelpdeskAnchorContainerProps = {
  appId: string;
  isUsingAda: boolean;
  isUsingStrada: boolean;
  screen: string;
  widgetIsMessengerVersion: boolean;
};

type HelpdeskAnchorStyle = CSSProperties & {
  bottom: string;
};

const resolveHelpdeskAnchorPreset = ({
  isUsingAda,
  isUsingStrada,
  widgetIsMessengerVersion,
}: Pick<
  HelpdeskAnchorContainerProps,
  'isUsingAda' | 'isUsingStrada' | 'widgetIsMessengerVersion'
>) => {
  if (isUsingAda || isUsingStrada) {
    return HELPDESK_ANCHOR_PRESETS.ada;
  }

  if (widgetIsMessengerVersion) {
    return HELPDESK_ANCHOR_PRESETS.messenger;
  }

  return HELPDESK_ANCHOR_PRESETS.default;
};

export const HelpdeskAnchorContainer: FC<HelpdeskAnchorContainerProps> = ({
  appId,
  screen,
  isUsingAda,
  isUsingStrada,
  widgetIsMessengerVersion,
}) => {
  const resolvedHelpdeskBottomOffsetPx = useResolvedHelpdeskBottomOffset();

  const helpdeskAnchorPreset = useMemo(
    () =>
      resolveHelpdeskAnchorPreset({
        isUsingAda,
        isUsingStrada,
        widgetIsMessengerVersion,
      }),
    [isUsingAda, isUsingStrada, widgetIsMessengerVersion]
  );

  const helpdeskAnchorStyle = useMemo<HelpdeskAnchorStyle>(() => {
    const bottomPx =
      resolvedHelpdeskBottomOffsetPx > 0
        ? resolvedHelpdeskBottomOffsetPx
        : helpdeskAnchorPreset.fallbackBottom;

    return {
      bottom: `${bottomPx}px`,
    };
  }, [helpdeskAnchorPreset, resolvedHelpdeskBottomOffsetPx]);

  return (
    <div className={styles.helpdeskAnchorContainer} style={helpdeskAnchorStyle}>
      <Helpdesk appId={appId} screen={screen} />
    </div>
  );
};
